/**
 * Recent Search Storage Utilities
 *
 * Handles reading/writing recent searches to localStorage in a format
 * compatible with mi-shop-components, and transforming data for the /sessions API.
 */

import type { NLSGeoCodeInfo, NLSAmpRequest } from '../services/nlsSearch.types';
import type {
  MiRecentSearchEntry,
  MiRecentlyViewedStorage,
  RecentSearchAPI,
  RecentlyViewedAPI,
  DestinationType,
} from '../types/personalization.types';

const RECENT_SEARCH_KEY = 'miRecentSearch';
const RECENTLY_VIEWED_KEY = 'miRecentlyViewedProperties';
const MAX_RECENT_SEARCHES_FOR_API = 5;
const MAX_RECENTLY_VIEWED_FOR_API = 5;

/**
 * Mapping of brand codes to brand names for the /sessions API payload
 */
const BRAND_CODE_TO_NAME: Record<string, string> = {
  AR: 'AC Hotels',
  AL: 'Aloft by Marriott',
  BA: 'Apartments by Marriott Bonvoy',
  AK: 'Autograph Collection',
  BG: 'Bvlgari',
  CM: 'citizenM',
  XE: 'City Express by Marriott',
  CY: 'Courtyard',
  DE: 'Delta Hotels',
  DS: 'Design Hotels™',
  EB: 'EDITION',
  EL: 'Element by Marriott',
  FI: 'Fairfield Inn By Marriott',
  FP: 'Four Points by Sheraton',
  XF: 'Four Points Flex by Sheraton',
  GE: 'Gaylord Hotels',
  JW: 'JW Marriott',
  MD: 'Le Méridien',
  ER: 'Marriott Executive Apartments',
  MC: 'Marriott Hotels & Resorts',
  MV: 'Marriott Vacation Club Intl',
  MG: 'MGM Collection with Marriott Bonvoy',
  OX: 'Moxy Hotels',
  OC: 'Outdoor Collection by Marriott Bonvoy',
  PR: 'Protea Hotels',
  BR: 'Renaissance Hotels',
  RI: 'Residence Inn',
  SE: 'Series by Marriott',
  SI: 'Sheraton',
  SH: 'SpringHill Suites',
  XR: 'St. Regis',
  RS: 'StudioRes by Marriott',
  LC: 'The Luxury Collection',
  RZ: 'The Ritz-Carlton',
  TS: 'TownePlace Suites By Marriott',
  TX: 'Tribute Portfolio',
  WH: 'W Hotels',
  WI: 'Westin Hotels & Resorts',
};

/**
 * Constructs the display label for a recent search entry.
 * - For city searches: "City, State, Country"
 * - For state/country searches: Uses address field or constructs from available parts
 */
function constructLabel(
  city: string | undefined,
  state: string | undefined,
  country: string | undefined,
  address: string | undefined
): string {
  if (city) {
    return [city, state, country].filter(Boolean).join(', ');
  }
  return address || [state, country].filter(Boolean).join(', ') || '';
}

/**
 * Converts a date string from YYYY-MM-DD to ISO 8601 timestamp format
 * that shop expects: YYYY-MM-DDTHH:mm:ss+0000
 */
function toShopDateFormat(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return `${dateStr}T00:00:00+0000`;
}

/**
 * Extracts adult count from guest counts array.
 * Adults are entries where age is null or >= 18.
 */
function extractAdultCount(
  guestCounts: Array<{ age?: number | null; count?: number | null }> | null | undefined
): number {
  if (!guestCounts || guestCounts.length === 0) return 1;

  const adultCount = guestCounts
    .filter(g => g.age === null || g.age === undefined || g.age >= 18)
    .reduce((sum, g) => sum + (g.count || 0), 0);

  return adultCount || 1;
}

/**
 * Extracts children count from guest counts array.
 * Children are entries where age is defined and < 18.
 */
function extractChildrenCount(
  guestCounts: Array<{ age?: number | null; count?: number | null }> | null | undefined
): number {
  if (!guestCounts || guestCounts.length === 0) return 0;

  return guestCounts
    .filter(g => g.age !== null && g.age !== undefined && g.age < 18)
    .reduce((sum, g) => sum + (g.count || 0), 0);
}

/**
 * Extracts children ages from guest counts array.
 */
function extractChildrenAges(
  guestCounts: Array<{ age?: number | null; count?: number | null }> | null | undefined
): number[] {
  if (!guestCounts || guestCounts.length === 0) return [];

  const ages: number[] = [];
  guestCounts
    .filter(g => g.age !== null && g.age !== undefined && g.age < 18)
    .forEach(g => {
      const count = g.count || 1;
      for (let i = 0; i < count; i++) {
        ages.push(g.age as number);
      }
    });

  return ages;
}

/**
 * Calculates length of stay from start and end dates.
 */
function calculateLengthOfStay(startDate: string | null | undefined, endDate: string | null | undefined): number {
  if (!startDate || !endDate) return 1;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 1;
}

/**
 * Adds a chat search result to the recent searches in localStorage.
 * Skips if geoCodeInfo is null (multi-location searches).
 *
 * @param geoCodeInfo - Location information from NLS response
 * @param ampRequest - Search parameters from NLS response
 */
export function addChatSearchToRecentSearches(
  geoCodeInfo: NLSGeoCodeInfo | null | undefined,
  ampRequest: NLSAmpRequest | null | undefined
): void {
  if (!geoCodeInfo?.details?.location) {
    return;
  }

  const location = geoCodeInfo.details.location;
  const { city, state, country, address, latitude, longitude } = location;

  const label = constructLabel(city, state, country, address);
  if (!label) {
    return;
  }

  const roomStay = ampRequest?.productQueriesHospitality?.roomStayQueryProduct?.baseHospitalityFacilityQuery;
  const timespan = roomStay?.timespan;
  const guestCounts = roomStay?.guestCounts;

  const numberOfAdults = extractAdultCount(guestCounts);
  const numberOfChildren = extractChildrenCount(guestCounts);
  const childrenAges = extractChildrenAges(guestCounts);
  const roomsCount = roomStay?.numberOfRooms || 1;
  const lengthOfStay = calculateLengthOfStay(timespan?.startDate, timespan?.endDate);

  const entry: MiRecentSearchEntry = {
    label,
    city: city || '',
    poiname: '',
    state: state || '',
    country: country || '',
    stateprovincedisplayname: '',
    destinationType: (geoCodeInfo.details.destinationType || '').toLowerCase(),
    countryname: '',
    airportcode: '',
    value: label,
    geocode: latitude && longitude ? `${latitude},${longitude}` : '',
    searchtype: 'recent',
    analytics: '{"location":"chatSearch","sendNow":"true","description":"Recent Search"}',
    placeId: geoCodeInfo.placeId || '',
    primaryDescription: '',
    secondaryDescription: '',
    latitude: latitude?.toString() || '',
    longitude: longitude?.toString() || '',
    radius: '',
    address: address || '',
    postalCode: '',
    types: '',
    website: '',
    flexible: false,
    lengthOfStay,
    fromDate: toShopDateFormat(timespan?.startDate),
    toDate: toShopDateFormat(timespan?.endDate),
    guests: numberOfAdults + numberOfChildren,
    numberOfAdults,
    numberOfChildren,
    roomsCount,
    childrenAges,
  };

  try {
    const existingJson = localStorage.getItem(RECENT_SEARCH_KEY);
    const existingEntries: MiRecentSearchEntry[] = existingJson ? JSON.parse(existingJson) : [];

    // Append to end like shop does - no deduplication, no limit
    existingEntries.push(entry);

    localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(existingEntries));
  } catch (error) {
    console.error('Failed to save recent search to localStorage:', error);
  }
}

/**
 * Converts a destination type string to the API enum format.
 */
function toDestinationType(type: string | undefined): DestinationType | undefined {
  if (!type) return undefined;

  const normalized = type.toLowerCase();
  const validTypes: DestinationType[] = ['city', 'hotel', 'airport', 'state', 'country', 'poi'];

  return validTypes.includes(normalized as DestinationType) ? (normalized as DestinationType) : undefined;
}

/**
 * Converts a shop date format (YYYY-MM-DDTHH:mm:ss+0000) to ISO date (YYYY-MM-DD).
 */
function toISODate(shopDate: string | undefined): string | undefined {
  if (!shopDate) return undefined;
  return shopDate.split('T')[0] || undefined;
}

/**
 * Gets today's date in ISO format (YYYY-MM-DD).
 */
function getTodayISODate(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Reads recent searches from localStorage and transforms them
 * to the format expected by the /sessions API.
 * Returns the most recent entries (up to MAX_RECENT_SEARCHES_FOR_API).
 *
 * @returns Array of recent searches in API format
 */
export function getRecentSearchesForAPI(): RecentSearchAPI[] {
  try {
    const existingJson = localStorage.getItem(RECENT_SEARCH_KEY);
    if (!existingJson) return [];

    const entries: MiRecentSearchEntry[] = JSON.parse(existingJson);

    // Take the most recent entries (from the end since shop appends to end)
    const recentEntries = entries.slice(-MAX_RECENT_SEARCHES_FOR_API);

    return recentEntries.map(entry => ({
      destination: entry.label,
      city: entry.city || undefined,
      state: entry.state || undefined,
      country: entry.country || undefined,
      destination_type: toDestinationType(entry.destinationType),
      check_in: toISODate(entry.fromDate),
      check_out: toISODate(entry.toDate),
      flexible_dates: entry.flexible || false,
      adults: entry.numberOfAdults || 1,
      children: entry.numberOfChildren || 0,
      rooms: entry.roomsCount || 1,
      searched_on: getTodayISODate(),
    }));
  } catch (error) {
    console.error('Failed to read recent searches from localStorage:', error);
    return [];
  }
}

/**
 * Reads recently viewed properties from localStorage and transforms them
 * to the format expected by the /sessions API.
 * Returns the most recent entries (up to MAX_RECENTLY_VIEWED_FOR_API).
 *
 * Note: This is read-only - chat does not write to miRecentlyViewedProperties.
 * The data is written by mi-book-components when users view hotel pages.
 *
 * @returns Array of recently viewed properties in API format
 */
export function getRecentlyViewedForAPI(): RecentlyViewedAPI[] {
  try {
    const json = localStorage.getItem(RECENTLY_VIEWED_KEY);
    if (!json) return [];

    const data: MiRecentlyViewedStorage = JSON.parse(json);
    const entries = data?.recentlyViewedProperties || [];

    // Take the first N entries (most recent are at the start - shop uses splice(0, 0, ...))
    return entries.slice(0, MAX_RECENTLY_VIEWED_FOR_API).map(entry => ({
      property_code: entry.marshaCode,
      hotel_name: entry.name || undefined,
      hotel_brand: entry.marshaBrandCode
        ? BRAND_CODE_TO_NAME[entry.marshaBrandCode.toUpperCase()] || entry.marshaBrandCode.toUpperCase()
        : undefined,
      country: entry.country || undefined,
      viewed_on: entry.createdAt?.split('T')[0] || undefined,
    }));
  } catch (error) {
    console.error('Failed to read recently viewed from localStorage:', error);
    return [];
  }
}
